import { getSocialConnector, getSocialPlatformModule } from '@lib/social/scheduling/platforms';
import {
  parseSocialConnectResult,
  SOCIAL_CONNECT_DENIED,
  stripSocialConnectResult,
} from '@lib/utils/socialConnectReturn';
import { showToast } from '@lib/utils/toastmaster';
import { socialConnectionsStore } from '@stores/SocialConnections';
import { trackingStore } from '@stores/Tracking';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Consumes the outcome the connect flow put in the query string, once. The
 * platform sends the browser back to whichever page started the connect
 * (`returnTo`), so this mounts once in the nav shell rather than on each
 * page. Only the result params are stripped, before anything else, so a
 * refresh or a re-render cannot replay the toast and the page's own params
 * (the schedule's month and view) survive. The pages themselves learn of the
 * new account through the connections store, which is refetched here.
 */
export function useSocialConnectReturn() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const result = parseSocialConnectResult(searchParams);
    if (!result) return;
    setSearchParams(stripSocialConnectResult(searchParams), { replace: true });

    const module = getSocialPlatformModule(result.platform);
    const platformLabel = getSocialConnector(result.platform)?.label ?? result.platform;
    if (result.outcome === 'error') {
      trackingStore.socialConnectFailed({
        platform: result.platform,
        errorCode: result.errorCode,
      });
      if (result.errorCode !== SOCIAL_CONNECT_DENIED) {
        showToast('social-connect-failed', {
          platformLabel,
          errorCode: result.errorCode,
          noAccountsNote: module?.noAccountsNote ?? null,
        });
      }
      return;
    }

    trackingStore.socialConnectCompleted({ platform: result.platform });
    const toastConnected = () => {
      const connections = socialConnectionsStore.data;
      showToast('social-connect-success', {
        platformLabel,
        accountNoun: module?.accountNoun ?? 'account',
        count: result.connectionIds.length,
        // The names the list holds for the new ids; fewer than `count` when the refetch failed or a name is missing.
        names: result.connectionIds.flatMap((id) => {
          const name = connections?.find((c) => c.id === id)?.displayName;
          return name ? [name] : [];
        }),
      });
    };
    // A failed refetch is already on screen through `socialConnectionsStore.error`;
    // the account is connected either way, so the toast still fires.
    socialConnectionsStore.update().then(toastConnected, toastConnected);
  }, [searchParams, setSearchParams]);
}
